import * as PIXI from 'pixi.js'
import { AnimatedGIF } from '@pixi/gif'
import { useProjectStore, ScaleType } from '@/store/project-store'
import { useApplicationStore } from '@/store/application-store'
import useLayers from '@/composables/UseLayers'
import { Layers } from '@/static/Layers'

export interface BackgroundCanvasState {
  url: string
  scale: ScaleType
  sprite: PIXI.Sprite | AnimatedGIF | null
}
class BackgroundLoadError extends Error {}

export function useBackgroundCanvas () {
  const projectStore = useProjectStore()
  const applicationStore = useApplicationStore()
  const { clearLayer } = useLayers()

  const handleBackgroundLoadError = (e: unknown, backgroundCanvasState: BackgroundCanvasState) => {
    if (e instanceof BackgroundLoadError) {
      applicationStore.pushSnackbarMessage({
        header: 'Oops something went wrong',
        state: 'error'
      })
      projectStore.setBackground(backgroundCanvasState.url, backgroundCanvasState.scale)
      applicationStore.isBackgroundLoading = false
    } 
    console.error(e)
  }

  const addBackground = async (
    application: PIXI.Application,
    backgroundLoader: PIXI.Loader,
    backgroundLayer: PIXI.Container,
    backgroundCanvasState: BackgroundCanvasState
  ): Promise<void> => {
    applicationStore.isBackgroundLoading = true

    let urlToLoad = projectStore.background.backgroundUrl
    try {
      if (!urlToLoad.startsWith('blob:')) {
        // Perform a HEAD request to determine if the URL is to a redirect
        const headResponse = await fetch(urlToLoad, {
          method: 'HEAD',
          mode: 'cors',
          cache: 'no-store' // Make sure requests are not cached in any way - redirects may expire for example
        })

        if (!headResponse.ok) {
          throw new BackgroundLoadError('Resource is unavailable')
        }

        if (headResponse.redirected) {
          // If the link is to a redirect, use the redirected link instead of the original link.
          // This is due to some browsers not being compatible with CORS with redirects, even if the redirects
          // send the appropriate headers. This is an issue in Safari where textures from a redirected source
          // throws a security error.
          urlToLoad = headResponse.url
        }
      }

      if (backgroundLoader.loading) {
        backgroundLoader.reset()
      }

      if (backgroundLoader.resources['background']) {
        // Delete any existing background loader resources - the loader doesn't allow replacement of the same resource
        delete backgroundLoader.resources['background']
      }

      // Load the background
      backgroundLoader.add('background', urlToLoad, (resource: PIXI.LoaderResource) => {
        if (resource.error || !(resource.animation || resource.texture)) {
          if (!resource.error) {
            console.error(`Incompatible resource. Loader cannot load: ${projectStore.background.backgroundUrl}`)
          }
          resource.error =  new BackgroundLoadError('Unable to load Resource')
        }
        
        if (resource.animation) {
          // `animation` is populated if it is a GIF
          backgroundCanvasState.sprite = resource.animation
        } else if (resource.texture) {
          // `texture` is populated if it is a supported video or image
          backgroundCanvasState.sprite = PIXI.Sprite.from(resource.texture)
        }

        clearLayer(Layers.BACKGROUND)

        if (backgroundCanvasState.sprite) {
          // Add loaded sprite to the stage furthest at the back (index 0)
          backgroundLayer.addChildAt(backgroundCanvasState.sprite, 0)

          // Deselect on click of background
          backgroundCanvasState.sprite.interactive = true
          backgroundCanvasState.sprite.addListener('pointertap', () => {
            applicationStore.deselectAllEntities()
          })
        }

        applicationStore.isBackgroundLoading = false

        // Make sure the background is filled or fitted according to toggled state
        backgroundCanvasState.scale = scaleBackground(application, backgroundCanvasState.sprite)

        backgroundCanvasState.url = projectStore.background.backgroundUrl
      })
      backgroundLoader.onError.add((e) => {
        handleBackgroundLoadError(e, backgroundCanvasState)
      })
      backgroundLoader.load(() => {
        if (!applicationStore.isPreviewCanvasLoaded) {
          applicationStore.isPreviewCanvasLoaded = true
        }
      })
    } catch (e) {
      handleBackgroundLoadError(e, backgroundCanvasState)
    }
  }

  const scaleBackground = (
    application: PIXI.Application,
    backgroundSprite: PIXI.Sprite | AnimatedGIF | null
  ): ScaleType => {
    if (projectStore.background.backgroundScale === 'fill') {
      fillBackground(application, backgroundSprite)
    } else {
      fitBackground(application, backgroundSprite)
    }
    return projectStore.background.backgroundScale
  }

  const fillBackground = (
    application: PIXI.Application,
    backgroundSprite: PIXI.Sprite | AnimatedGIF | null
  ): void => {
    if (backgroundSprite) {
      // Calculate ratios of the canvas relative to the background's dimensions
      const widthRatio = projectStore.resolution.width / backgroundSprite.texture.width
      const heightRatio = projectStore.resolution.height / backgroundSprite.texture.height

      // Choose the larger ratio - this is the maximum that the background needs to scale to match both the dimensions
      // of the canvas (or the minimum if the background is larger than the canvas)
      const scalingFactor = Math.max(widthRatio, heightRatio)

      // Scale the background
      backgroundSprite.scale.set(scalingFactor, scalingFactor)

      // Set the origin of the sprite to be in the center
      backgroundSprite.anchor.set(0.5, 0.5)

      // Set the position of the sprite to be the center of the canvas
      backgroundSprite.position.set(application.renderer.width / 2, application.renderer.height / 2)
    }
  }

  const fitBackground = (
    application: PIXI.Application,
    backgroundSprite: PIXI.Sprite | AnimatedGIF | null
  ): void => {
    if (backgroundSprite) {
      // Calculate ratios of the canvas relative to the background's dimensions
      const widthRatio = projectStore.resolution.width / backgroundSprite.texture.width
      const heightRatio = projectStore.resolution.height / backgroundSprite.texture.height

      // Choose the smaller ratio - this is the maximum that the background needs to scale down to match both the dimensions of the canvas
      const scalingFactor = Math.min(widthRatio, heightRatio)

      // Scale the background
      backgroundSprite.scale.set(scalingFactor, scalingFactor)

      // Set the origin of the sprite to be in the center
      backgroundSprite.anchor.set(0.5, 0.5)

      // Set the position of the sprite to be the center of the canvas
      backgroundSprite.position.set(application.renderer.width / 2, application.renderer.height / 2)
    }
  }

  return {
    addBackground,
    scaleBackground
  }
}
